import { isEqual } from 'lodash';
import * as uuid from 'uuid';

import { DisplayElementModel } from '../../../Common/PageBuilder/models/display-element.model';
import { MentionModel } from '../../../api/mentions/model/mention.model';
import { MonitoringBackEndDataSourceModel } from '../../Shared/models/monitoring-back-end-data-source.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { DisplayElementWithSearchCriteriaModel } from './display-element-with-search-criteria.model';
import { MentionCustomizationModel } from '../../Shared/models/mention-customization.model';
import { MonitoringSeriesTypeEnum } from '../../Shared/enums/monitoring-series-type.enum';
import { MentionSourceApiTypesEnum, monitoringSeriesTypeEnumToMentionSourceApiTypesEnum } from '../../../api/mentions/enum/mention-source-api-types.enum';
import { AdditionalSettings } from './additional-settings.model';

export class DynamicMentionListDisplayElementModel extends DisplayElementModel implements DisplayElementWithSearchCriteriaModel {
    declare styles: MentionCustomizationModel;

    declare isBoundToGlobalStyles: boolean;
    title: string;
    pageSize: number;
    filterMentionSearchModel: MentionSearchModel;
    // Filter search criteriaId
    searchCriteriaId: number;
    backendDataSource: MonitoringBackEndDataSourceModel;
    includeInsightsLink: boolean = false;
    sourceType: MentionSourceApiTypesEnum = MentionSourceApiTypesEnum.ALL_SAVED_COVERAGE;
    blockReferenceId: string;
    mentionPreviewCount: number;
    mentions: MentionModel[];
    useSearchDateFilter: boolean;

    additionalSettings = new AdditionalSettings();

    readonly dateRangeAllowed: boolean = true;

    constructor(
        heading?: string,
        backendDataSourceType?: MonitoringSeriesTypeEnum,
        backEndDataSourceId?: number,
        pageSize?: number,
        searchCriteriaId?: number,
        searchCriteria?: MentionSearchModel,
        styles?: any,
        blockReferenceId?: string,
        mentionPreviewCount?: number,
        automaticallyGenerated?: boolean
    ) {
        super();

        this.title = heading;

        this.sourceType = monitoringSeriesTypeEnumToMentionSourceApiTypesEnum.get(backendDataSourceType);

        if (pageSize === null || pageSize === undefined ) {
            this.pageSize = pageSize;
        } else {
            this.pageSize = pageSize || 10;
        }

        this.searchCriteriaId = searchCriteriaId;

        this.filterMentionSearchModel = searchCriteria;

        this.styles = new MentionCustomizationModel(styles);

        if (blockReferenceId) {
            this.blockReferenceId = blockReferenceId;
        } else {
            this.blockReferenceId = uuid.v4();
        }

        // for now we don't populate mentions
        this.mentions = [];
        this.isBoundToGlobalStyles = true;

        this.automaticallyGenerated = automaticallyGenerated;

        this.backendDataSource = new MonitoringBackEndDataSourceModel(
            backendDataSourceType,
            backEndDataSourceId
        );

        this.mentionPreviewCount = mentionPreviewCount;
    }

    canRender(): boolean {
        return !!this.title || !!this.backendDataSource.id;
    }

    clone(): DynamicMentionListDisplayElementModel {
        const newModel = new DynamicMentionListDisplayElementModel(
            this.title,
            this.backendDataSource.type,
            this.backendDataSource.id,
            this.pageSize,
            this.searchCriteriaId,
            this.filterMentionSearchModel,
            this.styles.clone(),
            uuid.v4(),
            this.mentionPreviewCount
        );
        newModel.backendDataSource = this.backendDataSource.clone();
        newModel.includeInsightsLink = this.includeInsightsLink;
        newModel.sourceType = this.sourceType;
        newModel.additionalSettings = this.additionalSettings;
        newModel.displayType = this.displayType;
        newModel.automaticallyGenerated = this.automaticallyGenerated;
        newModel.useSearchDateFilter = this.useSearchDateFilter;

        return newModel;
    }

    onUpdate(): boolean {
        if (this.backendDataSource) {
            this.sourceType = monitoringSeriesTypeEnumToMentionSourceApiTypesEnum.get(this.backendDataSource.type);
            if (!this.filterMentionSearchModel) {
                this.filterMentionSearchModel = new MentionSearchModel();
            }

            this.filterMentionSearchModel.searchCriteria = this.backendDataSource.filterSearchCriteria;
        }

        return false;
    }

    isEqual(other: DynamicMentionListDisplayElementModel): boolean {
        if (this.title !== other.title) {
            return false;
        }

        if (!this.backendDataSource.equals(other.backendDataSource, false)) {
            return false;
        }

        if (this.pageSize !== other.pageSize) {
            return false;
        }

        // If the block is using a saved style, just compare that it's still using the same saved style, if it's not using a saved style, compare the values
        if (
            this.styles.styleState &&
            Number.isInteger(this.styles.styleState)
        ) {
            if (this.styles.styleState !== other.styles?.styleState) {
                return false;
            }
        } else if (!this.styles.equals(other.styles, true)) {
            return false;
        }

        if (this.isBoundToGlobalStyles !== other.isBoundToGlobalStyles) {
            return false;
        }
        if (this.includeInsightsLink !== other.includeInsightsLink) {
            return false;
        }

        if (this.sourceType !== other.sourceType) {
            return false;
        }
        if (!isEqual(this.additionalSettings, other.additionalSettings)) {
            return false;
        }
        if (this.blockReferenceId !== other.blockReferenceId) {
            return false;
        }

        if (this.mentionPreviewCount !== other.mentionPreviewCount) {
            return false;
        }

        if (!isEqual(this.automaticallyGenerated, other.automaticallyGenerated)) {
            return false;
        }

        if (this.useSearchDateFilter !== other.useSearchDateFilter) {
            return false;
        }

        return true;
    }

    setSearchCriteria(searchCriteria: MentionSearchModel) {
        this.filterMentionSearchModel = searchCriteria;
        this.backendDataSource.filterSearchCriteria = this.filterMentionSearchModel.searchCriteria;
    }

    getSearchCriteria(): MentionSearchModel {
        if (!this.filterMentionSearchModel) {
            return null;
        }
        this.filterMentionSearchModel.searchCriteria = this.backendDataSource.filterSearchCriteria;

        return this.filterMentionSearchModel;
    }

    // Do nothing
    setInitialSourceSearchCriteria(searchCriteria: MentionSearchModel) {}
}
